import type { Ref } from "vue"
import { watch } from "vue"
import type { Swiper as SwiperInstance } from "swiper"
import { SpringConfig, useSpring } from "./use-spring"

export const SPRING_DURATION_MS = 600

const { config } = useSpring("snappy")
const springEasingCache = new Map<number, string>()

export function buildSpringEasing(durationMs: number): string {
  const key = Math.round(durationMs)
  if (springEasingCache.has(key)) return springEasingCache.get(key)!
  const { stiffness, damping } = config as SpringConfig
  const dt = 0.001
  const totalMs = Math.round(durationMs)
  const samples = 20
  let pos = 0
  let vel = 0
  let nextSample = 1
  const points: string[] = ["0"]

  for (let ms = 1; ms <= totalMs; ms++) {
    const force = (1 - pos) * stiffness - vel * damping
    vel += force * dt
    pos += vel * dt
    const targetMs = Math.round((nextSample / samples) * totalMs)
    if (ms === targetMs && nextSample < samples) {
      points.push(
        `${pos.toFixed(3)} ${Math.round((nextSample / samples) * 100)}%`,
      )
      nextSample++
    }
  }
  points.push("1")
  const result = `linear(${points.join(", ")})`
  springEasingCache.set(key, result)
  return result
}

export function useSwiperSpring(swiperRef: Ref<SwiperInstance | undefined>) {
  watch(
    swiperRef,
    swiper => {
      if (!swiper) return
      swiper.on("setTransition", (_swiper, duration) => {
        if (duration === 0) return
        const easing = buildSpringEasing(duration)
        _swiper.wrapperEl.style.transitionTimingFunction = easing
        _swiper.slides.forEach(slide => {
          slide.style.transitionTimingFunction = easing
        })
      })
    },
    { immediate: true },
  )
}
